import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useLocale, type Locale } from '../../hooks/useT';

function CGUBodyFr() {
  return (
    <>
      <h2>1. Objet</h2>
      <p>
        Les présentes Conditions Générales d&apos;Utilisation (CGU) régissent l&apos;accès et
        l&apos;utilisation du jeu en ligne <strong>Dezoom</strong>, accessible sur{' '}
        <a href="https://dezoom.eu">dezoom.eu</a>, édité par PrograGames (Florian Augeard).
        En accédant au jeu, tu acceptes ces conditions sans réserve. Si tu n&apos;acceptes pas
        ces conditions, tu ne dois pas utiliser le service.
      </p>
      <h2>2. Éditeur</h2>
      <p>
        PrograGames — Florian Augeard, entrepreneur individuel, 49000 Angers, France.
        SIREN 106 684 780. Contact : <Link to="/contact">formulaire de contact</Link> ou{' '}
        <a href="mailto:contact@dezoom.eu">contact@dezoom.eu</a>.
      </p>
      <h2>3. Accès au jeu</h2>
      <p>
        Dezoom est un jeu de devinettes photo dans le navigateur. Le défi du jour, les archives
        et les classements sont gratuits et financés par la publicité (Google AdSense).
        Un abonnement premium optionnel supprime les publicités et soutient le développement
        (voir <Link to="/cgv">CGV</Link>). Une connexion Internet est requise. Aucun
        téléchargement ni installation n&apos;est nécessaire.
      </p>
      <h2>4. Âge et capacité</h2>
      <p>
        Le jeu s&apos;adresse aux personnes de <strong>16 ans et plus</strong>. Si tu es mineur,
        l&apos;utilisation doit se faire avec l&apos;autorisation d&apos;un parent ou titulaire de l&apos;autorité parentale.
      </p>
      <h2>5. Comptes joueur et comptes invités</h2>
      <ul>
        <li>
          Tu peux jouer en <strong>invité</strong> : un compte invité temporaire est créé
          automatiquement (pseudo aléatoire type « Joueur #1234 »), sans email. Il permet de jouer
          au défi du jour, de figurer aux classements et de découvrir les archives récentes.
        </li>
        <li>
          Tu peux <strong>valider ton compte</strong> à tout moment (email ou Google) : ta
          progression invité (scores, historique) est alors conservée et rattachée à ton compte définitif.
        </li>
        <li>Tu es responsable de la confidentialité de ton accès (lien magique, session).</li>
        <li>Le pseudo doit être unique et ne pas porter atteinte aux droits de tiers.</li>
        <li>La progression (série, scores, classements) est liée au compte connecté.</li>
        <li>
          Les comptes invités inactifs pendant une longue période peuvent être supprimés ou
          anonymisés par l&apos;éditeur.
        </li>
      </ul>
      <h2>6. Fonctionnalités</h2>
      <ul>
        <li><strong>Défi du jour</strong> : une énigme officielle par jour, classement quotidien.</li>
        <li><strong>Archives</strong> : rejouer les énigmes passées ; la première partie compte pour le classement historique de l&apos;énigme, sans impact sur la série de victoires.</li>
        <li><strong>Classements</strong> : du jour, de la semaine, du mois et tous les temps.</li>
        <li><strong>Top dezoom</strong> : énigmes quotidiennes les plus aimées par la communauté.</li>
        <li><strong>Arène et ligues</strong> : modes compétitifs entre joueurs.</li>
        <li><strong>Premium</strong> : expérience sans publicité et soutien au développement.</li>
      </ul>
      <h2>7. Règles de conduite</h2>
      <p>Il est interdit notamment de :</p>
      <ul>
        <li>Utiliser des bots, scripts ou outils automatisés ;</li>
        <li>Exploiter des bugs pour obtenir un avantage ;</li>
        <li>Harceler d&apos;autres joueurs ;</li>
        <li>Contourner les systèmes de score ou de classement ;</li>
        <li>Usurper l&apos;identité d&apos;autrui ou de l&apos;éditeur ;</li>
        <li>Créer plusieurs comptes pour fausser les classements ;</li>
        <li>Tenter d&apos;accéder sans autorisation aux systèmes du service.</li>
      </ul>
      <h2>8. Signalement d&apos;abus</h2>
      <p>
        Tu peux signaler un pseudo inapproprié, un comportement abusif ou tout contenu
        problématique via le <Link to="/contact">formulaire de contact</Link> (motif
        « Signalement »). L&apos;éditeur examine les signalements et peut prendre les mesures
        décrites à l&apos;article 9.
      </p>
      <h2>9. Suspension et suppression de compte</h2>
      <ul>
        <li>
          En cas de violation des présentes CGU, l&apos;éditeur peut avertir, bloquer temporairement
          ou supprimer un compte, sans préavis en cas de manquement grave.
        </li>
        <li>
          Tu peux <strong>supprimer ton compte</strong> à tout moment depuis ta page profil
          (section « Supprimer mon compte »). La suppression anonymise tes données personnelles
          (email, pseudo, avatar) ; les scores anonymisés peuvent être conservés pour
          l&apos;intégrité des classements.
        </li>
        <li>La suppression du compte n&apos;ouvre droit à aucun remboursement des achats déjà consommés (voir <Link to="/cgv">CGV</Link>).</li>
      </ul>
      <h2>10. Notifications et communications</h2>
      <p>
        Avec ton accord, le service peut t&apos;envoyer des notifications push (rappel du défi du
        jour) et des emails liés à ton compte. Tu peux les désactiver à tout moment depuis les
        réglages de ton profil ou de ton navigateur. Les emails de connexion (lien magique) sont
        nécessaires au fonctionnement du compte.
      </p>
      <h2>11. Abonnement premium</h2>
      <p>
        L&apos;abonnement premium est un service numérique lié à ton compte,
        non transférable. Il supprime l&apos;affichage publicitaire sur le site.
        Conditions de résiliation et remboursement : voir <Link to="/cgv">CGV</Link>.
      </p>
      <h2>12. Propriété intellectuelle</h2>
      <p>
        Le jeu, ses énigmes, marques et contenus restent la propriété de PrograGames ou de ses concédants
        (photographes Pexels/Unsplash — voir <Link to="/credits">crédits photos</Link>).
        Toute reproduction ou exploitation non autorisée est interdite.
      </p>
      <h2>13. Données personnelles</h2>
      <p>
        Le traitement des données est décrit dans la{' '}
        <Link to="/confidentialite">politique de confidentialité</Link>.
      </p>
      <h2>14. Responsabilité et disponibilité</h2>
      <p>
        Le jeu est fourni « en l&apos;état ». PrograGames ne garantit pas un fonctionnement
        ininterrompu et peut suspendre temporairement le service pour maintenance. La
        responsabilité de l&apos;éditeur ne saurait être engagée pour les dommages indirects
        liés à l&apos;utilisation du service.
      </p>
      <h2>15. Modification des CGU</h2>
      <p>
        L&apos;éditeur peut modifier les présentes CGU. La version en vigueur est celle publiée
        sur cette page, avec sa date de mise à jour. En cas de modification substantielle, une
        information sera affichée sur le site. La poursuite de l&apos;utilisation vaut acceptation.
      </p>
      <h2>16. Droit applicable</h2>
      <p>
        Les présentes CGU sont soumises au <strong>droit français</strong>. En cas de litige,
        une solution amiable sera recherchée avant toute action judiciaire (voir aussi la
        médiation décrite dans les <Link to="/cgv">CGV</Link>).
      </p>
    </>
  );
}

function CGUBodyEn() {
  return (
    <>
      <h2>1. Purpose</h2>
      <p>
        These Terms of Use govern access to and use of the online game{' '}
        <strong>Dezoom</strong>, available at <a href="https://dezoom.eu">dezoom.eu</a>,
        published by PrograGames (Florian Augeard). By accessing the game, you accept these
        terms without reservation. If you do not accept them, you must not use the service.
      </p>
      <h2>2. Publisher</h2>
      <p>
        PrograGames — Florian Augeard, sole proprietor, 49000 Angers, France.
        SIREN 106 684 780. Contact: <Link to="/contact">contact form</Link> or{' '}
        <a href="mailto:contact@dezoom.eu">contact@dezoom.eu</a>.
      </p>
      <h2>3. Access to the game</h2>
      <p>
        Dezoom is a photo guessing game in the browser. The daily challenge, archives
        and leaderboards are free and funded by advertising (Google AdSense).
        An optional premium subscription removes ads and supports development
        (see <Link to="/cgv">Terms of sale</Link>). An Internet connection is required.
        No download or installation is needed.
      </p>
      <h2>4. Age and capacity</h2>
      <p>
        The game is intended for users aged <strong>16 and over</strong>. Minors must use the service
        with parental or guardian consent.
      </p>
      <h2>5. Player accounts and guest accounts</h2>
      <ul>
        <li>
          You may play as a <strong>guest</strong>: a temporary guest account is created
          automatically (random display name like &quot;Joueur #1234&quot;), without an email.
          It lets you play the daily challenge, appear on leaderboards and discover recent archives.
        </li>
        <li>
          You can <strong>validate your account</strong> at any time (email or Google): your guest
          progress (scores, history) is kept and attached to your permanent account.
        </li>
        <li>You are responsible for keeping your access credentials secure.</li>
        <li>Display names must be unique and must not infringe third-party rights.</li>
        <li>Progress (streak, scores, leaderboards) is tied to your logged-in account.</li>
        <li>Guest accounts inactive for a long period may be deleted or anonymized by the publisher.</li>
      </ul>
      <h2>6. Features</h2>
      <ul>
        <li><strong>Daily challenge</strong>: one official puzzle per day with a daily leaderboard.</li>
        <li><strong>Archives</strong>: replay past daily puzzles; your first attempt counts toward that puzzle&apos;s historical leaderboard, without affecting your win streak.</li>
        <li><strong>Leaderboards</strong>: today, this week, this month and all time.</li>
        <li><strong>Top dezoom</strong>: most liked daily puzzles in the community.</li>
        <li><strong>Arena and leagues</strong>: competitive modes between players.</li>
        <li><strong>Premium</strong>: ad-free experience and developer support.</li>
      </ul>
      <h2>7. Code of conduct</h2>
      <p>The following is prohibited, including but not limited to:</p>
      <ul>
        <li>Using bots, scripts or automated tools;</li>
        <li>Exploiting bugs for unfair advantage;</li>
        <li>Harassing other players;</li>
        <li>Circumventing score or leaderboard systems;</li>
        <li>Impersonating others or the publisher;</li>
        <li>Creating multiple accounts to distort leaderboards;</li>
        <li>Attempting unauthorized access to the service&apos;s systems.</li>
      </ul>
      <h2>8. Reporting abuse</h2>
      <p>
        You can report an inappropriate display name, abusive behavior or any problematic
        content through the <Link to="/contact">contact form</Link> (topic &quot;Report&quot;).
        The publisher reviews reports and may take the measures described in section 9.
      </p>
      <h2>9. Account suspension and deletion</h2>
      <ul>
        <li>
          In case of violation of these terms, the publisher may warn, temporarily block or
          delete an account, without notice in case of serious breach.
        </li>
        <li>
          You can <strong>delete your account</strong> at any time from your profile page
          (&quot;Delete my account&quot; section). Deletion anonymizes your personal data
          (email, display name, avatar); anonymized scores may be kept for leaderboard integrity.
        </li>
        <li>Account deletion does not entitle you to a refund of already consumed purchases (see <Link to="/cgv">Terms of sale</Link>).</li>
      </ul>
      <h2>10. Notifications and communications</h2>
      <p>
        With your consent, the service may send you push notifications (daily challenge reminder)
        and account-related emails. You can disable them at any time from your profile settings
        or your browser. Login emails (magic link) are required for account operation.
      </p>
      <h2>11. Premium subscription</h2>
      <p>
        Premium is a digital service tied to your account, non-transferable.
        It removes advertising on the site.
        Cancellation and refund terms: see <Link to="/cgv">Terms of sale</Link>.
      </p>
      <h2>12. Intellectual property</h2>
      <p>
        The game, puzzles, branding and content remain the property of PrograGames or its licensors
        (Pexels/Unsplash photographers — see <Link to="/credits">photo credits</Link>).
        Any unauthorized reproduction or exploitation is prohibited.
      </p>
      <h2>13. Personal data</h2>
      <p>
        Data processing is described in our{' '}
        <Link to="/confidentialite">privacy policy</Link>.
      </p>
      <h2>14. Liability and availability</h2>
      <p>
        The game is provided &quot;as is&quot;. PrograGames does not guarantee uninterrupted
        operation and may temporarily suspend the service for maintenance. The publisher shall
        not be liable for indirect damages related to the use of the service.
      </p>
      <h2>15. Changes to these terms</h2>
      <p>
        The publisher may amend these Terms of Use. The applicable version is the one published
        on this page with its update date. In case of substantial change, a notice will be shown
        on the site. Continued use constitutes acceptance.
      </p>
      <h2>16. Governing law</h2>
      <p>
        These Terms of Use are governed by <strong>French law</strong>. In case of dispute, an
        amicable solution will be sought before any legal action (see also mediation in the{' '}
        <Link to="/cgv">Terms of sale</Link>).
      </p>
    </>
  );
}

function CGVBodyFr() {
  return (
    <>
      <h2>1. Vendeur</h2>
      <p>
        <strong>Florian Augeard</strong> — PrograGames<br />
        Entrepreneur individuel · 49000 Angers, France<br />
        SIREN 106 684 780 · RCS Angers<br />
        Contact : <Link to="/contact">formulaire de contact</Link> ·{' '}
        <a href="mailto:contact@dezoom.eu">contact@dezoom.eu</a>
      </p>
      <h2>2. Produits</h2>
      <p>Les achats portent sur du <strong>contenu numérique</strong> activable immédiatement :</p>
      <table className="legal-table">
        <thead>
          <tr><th>Produit</th><th>Description</th><th>Prix (EUR)</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>Dezoom Premium</td>
            <td>Expérience sans publicité, soutien au développement</td>
            <td>Selon l&apos;offre en vigueur, affichée avant paiement (Stripe)</td>
          </tr>
        </tbody>
      </table>
      <h2>3. Prix et taxes</h2>
      <p>
        Les prix sont indiqués en euros, toutes taxes comprises, sur la page{' '}
        <Link to="/premium">Premium</Link> et récapitulés avant validation du paiement.
        Le vendeur peut modifier ses prix à tout moment ; le prix applicable est celui
        affiché au moment de la commande.
      </p>
      <h2>4. Commande et paiement</h2>
      <p>
        Paiement sécurisé via <strong>Stripe</strong> (carte bancaire). Un compte connecté est
        requis. Le vendeur ne stocke aucun numéro de carte. La commande est confirmée après
        validation du paiement par Stripe.
      </p>
      <h2>5. Durée, renouvellement et résiliation</h2>
      <ul>
        <li>Les conditions de durée (période d&apos;accès ou abonnement) sont précisées sur la page <Link to="/premium">Premium</Link> avant l&apos;achat.</li>
        <li>
          En cas d&apos;abonnement à reconduction, tu peux résilier à tout moment : l&apos;accès premium
          reste actif jusqu&apos;à la fin de la période déjà payée, sans remboursement au prorata.
        </li>
        <li>La résiliation s&apos;effectue depuis ton profil ou via le <Link to="/contact">formulaire de contact</Link> (motif « Facturation »).</li>
      </ul>
      <h2>6. Livraison</h2>
      <p>Activation <strong>immédiate</strong> après confirmation du paiement, sur le compte connecté au moment de l&apos;achat.</p>
      <h2>7. Droit de rétractation</h2>
      <p>
        Conformément à l&apos;article L221-28 du Code de la consommation, le droit de rétractation
        de <strong>14 jours</strong> ne s&apos;applique pas au contenu numérique fourni immédiatement
        avec ton accord exprès et renoncement au droit de rétractation, recueillis au moment de
        l&apos;achat. À défaut de renoncement, le délai de 14 jours court à compter de l&apos;achat.
      </p>
      <h2>8. Remboursements</h2>
      <ul>
        <li>Contenu non livré (échec d&apos;activation) : remboursement ou activation, au choix de l&apos;acheteur.</li>
        <li>Contenu déjà activé : pas de remboursement sauf obligation légale.</li>
        <li>Demandes via le <Link to="/contact">formulaire de contact</Link> (motif « Facturation »).</li>
      </ul>
      <h2>9. Garanties légales</h2>
      <p>
        Le contenu numérique bénéficie de la garantie légale de conformité (articles L224-25-12
        et suivants du Code de la consommation). En cas de défaut de conformité, tu peux demander
        la mise en conformité du service, ou à défaut une réduction du prix ou la résolution du contrat.
      </p>
      <h2>10. Réclamations et médiation</h2>
      <p>
        Toute réclamation peut être adressée via le <Link to="/contact">formulaire de contact</Link>.
        À défaut de résolution amiable sous 60 jours, tu peux saisir gratuitement le médiateur de la
        consommation <strong>CM2C</strong> —{' '}
        <a href="https://www.cm2c.net/" target="_blank" rel="noopener noreferrer">www.cm2c.net</a>{' '}
        (CM2C, 49 rue de Ponthieu, 75008 Paris).
      </p>
      <h2>11. Litiges</h2>
      <p>
        Plateforme européenne de règlement en ligne des litiges (ODR) :{' '}
        <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">
          ec.europa.eu/consumers/odr
        </a>
        . Les présentes CGV sont soumises au droit français.
      </p>
      <h2>12. Modification des CGV</h2>
      <p>
        Le vendeur peut modifier les présentes CGV. Les conditions applicables à une commande
        sont celles en vigueur au jour de la commande.
      </p>
      <h2>13. CGU</h2>
      <p>L&apos;utilisation du jeu reste soumise aux <Link to="/cgu">CGU</Link>.</p>
    </>
  );
}

function CGVBodyEn() {
  return (
    <>
      <h2>1. Seller</h2>
      <p>
        <strong>Florian Augeard</strong> — PrograGames<br />
        Sole proprietor · 49000 Angers, France<br />
        SIREN 106 684 780 · RCS Angers<br />
        Contact: <Link to="/contact">contact form</Link> ·{' '}
        <a href="mailto:contact@dezoom.eu">contact@dezoom.eu</a>
      </p>
      <h2>2. Products</h2>
      <p>Purchases cover <strong>digital content</strong> activated immediately on your account:</p>
      <table className="legal-table">
        <thead>
          <tr><th>Product</th><th>Description</th><th>Price (EUR)</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>Dezoom Premium</td>
            <td>Ad-free experience, developer support</td>
            <td>As shown at checkout (Stripe)</td>
          </tr>
        </tbody>
      </table>
      <h2>3. Prices and taxes</h2>
      <p>
        Prices are shown in euros, all taxes included, on the <Link to="/premium">Premium</Link>{' '}
        page and summarized before payment. The seller may change prices at any time; the
        applicable price is the one displayed at the time of the order.
      </p>
      <h2>4. Order and payment</h2>
      <p>
        Secure payment via <strong>Stripe</strong> (card). A logged-in account is required.
        The seller does not store any card numbers. The order is confirmed once Stripe validates
        the payment.
      </p>
      <h2>5. Duration, renewal and cancellation</h2>
      <ul>
        <li>Duration terms (access period or subscription) are stated on the <Link to="/premium">Premium</Link> page before purchase.</li>
        <li>
          For auto-renewing subscriptions, you can cancel at any time: premium access remains
          active until the end of the paid period, without pro-rata refund.
        </li>
        <li>Cancellation is done from your profile or through the <Link to="/contact">contact form</Link> (topic &quot;Billing&quot;).</li>
      </ul>
      <h2>6. Delivery</h2>
      <p><strong>Immediate</strong> activation after payment confirmation, on the account logged in at the time of purchase.</p>
      <h2>7. Right of withdrawal</h2>
      <p>
        Under article L221-28 of the French Consumer Code, the <strong>14-day</strong> withdrawal
        right does not apply to digital content supplied immediately with your express consent
        and waiver of the withdrawal right, both collected at purchase time. Absent such waiver,
        the 14-day period runs from the purchase date.
      </p>
      <h2>8. Refunds</h2>
      <ul>
        <li>Undelivered content (activation failure): refund or activation, at the buyer&apos;s choice.</li>
        <li>Already activated content: no refund except where required by law.</li>
        <li>Requests through the <Link to="/contact">contact form</Link> (topic &quot;Billing&quot;).</li>
      </ul>
      <h2>9. Legal warranties</h2>
      <p>
        Digital content benefits from the French legal warranty of conformity (articles
        L224-25-12 et seq. of the Consumer Code). In case of lack of conformity, you may request
        that the service be brought into conformity, or failing that a price reduction or
        termination of the contract.
      </p>
      <h2>10. Complaints and mediation</h2>
      <p>
        Any complaint can be sent through the <Link to="/contact">contact form</Link>. If no
        amicable solution is found within 60 days, you may refer the matter free of charge to the
        consumer mediator <strong>CM2C</strong> —{' '}
        <a href="https://www.cm2c.net/" target="_blank" rel="noopener noreferrer">www.cm2c.net</a>{' '}
        (CM2C, 49 rue de Ponthieu, 75008 Paris, France).
      </p>
      <h2>11. Disputes</h2>
      <p>
        EU Online Dispute Resolution platform:{' '}
        <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">
          ec.europa.eu/consumers/odr
        </a>
        . These terms of sale are governed by French law.
      </p>
      <h2>12. Changes to these terms</h2>
      <p>
        The seller may amend these terms of sale. The terms applicable to an order are those in
        force on the day of the order.
      </p>
      <h2>13. Terms of use</h2>
      <p>Use of the game remains subject to the <Link to="/cgu">Terms of use</Link>.</p>
    </>
  );
}

function MentionsBodyFr() {
  return (
    <>
      <h2>Éditeur du site et du jeu</h2>
      <p>
        <strong>Florian Augeard</strong><br />
        Entrepreneur individuel — <strong>PrograGames</strong><br />
        49000 Angers, France<br />
        SIREN 106 684 780 · RCS Angers<br />
        Site éditeur :{' '}
        <a href="https://progragames.com/" target="_blank" rel="noopener noreferrer">progragames.com</a>
      </p>
      <h2>Directeur de la publication</h2>
      <p>Florian Augeard, en qualité d&apos;éditeur du site.</p>
      <h2>Contact</h2>
      <p>
        <Link to="/contact">Formulaire de contact</Link> ·{' '}
        <a href="mailto:contact@dezoom.eu">contact@dezoom.eu</a>
      </p>
      <h2>Hébergement</h2>
      <p>
        <strong>Scaleway SAS</strong><br />
        8 rue de la Ville l&apos;Évêque, 75008 Paris, France<br />
        <a href="https://www.scaleway.com/" target="_blank" rel="noopener noreferrer">www.scaleway.com</a>
      </p>
      <h2>Propriété intellectuelle</h2>
      <p>
        La structure du site, le jeu, ses textes, logos et éléments graphiques sont la propriété
        de PrograGames, sauf mention contraire. Les photographies utilisées dans les énigmes
        proviennent des plateformes <strong>Pexels</strong> et <strong>Unsplash</strong> et restent
        la propriété de leurs auteurs respectifs — chaque photographe est crédité sur la page{' '}
        <Link to="/credits">Crédits photos</Link>. Toute reproduction non autorisée du site ou de
        son contenu est interdite.
      </p>
      <h2>Signalement de contenu</h2>
      <p>
        Pour signaler un contenu illicite ou portant atteinte à tes droits (photo, pseudo,
        comportement), utilise le <Link to="/contact">formulaire de contact</Link> (motif
        « Signalement ») en décrivant précisément le contenu concerné. L&apos;éditeur traitera le
        signalement dans les meilleurs délais.
      </p>
      <h2>Données personnelles et cookies</h2>
      <p>
        Les traitements de données et la gestion des cookies sont décrits dans la{' '}
        <Link to="/confidentialite">politique de confidentialité</Link>. Tu peux gérer tes
        préférences publicitaires via le lien « Gérer les cookies » en bas de page.
      </p>
      <h2>Documents contractuels</h2>
      <ul>
        <li><Link to="/cgu">Conditions générales d&apos;utilisation (CGU)</Link></li>
        <li><Link to="/cgv">Conditions générales de vente (CGV)</Link></li>
        <li><Link to="/confidentialite">Politique de confidentialité</Link></li>
      </ul>
    </>
  );
}

function MentionsBodyEn() {
  return (
    <>
      <h2>Site and game publisher</h2>
      <p>
        <strong>Florian Augeard</strong><br />
        Sole proprietor — <strong>PrograGames</strong><br />
        49000 Angers, France<br />
        SIREN 106 684 780 · RCS Angers<br />
        Publisher website:{' '}
        <a href="https://progragames.com/" target="_blank" rel="noopener noreferrer">progragames.com</a>
      </p>
      <h2>Publication director</h2>
      <p>Florian Augeard, as publisher of the site.</p>
      <h2>Contact</h2>
      <p>
        <Link to="/contact">Contact form</Link> ·{' '}
        <a href="mailto:contact@dezoom.eu">contact@dezoom.eu</a>
      </p>
      <h2>Hosting</h2>
      <p>
        <strong>Scaleway SAS</strong><br />
        8 rue de la Ville l&apos;Évêque, 75008 Paris, France<br />
        <a href="https://www.scaleway.com/" target="_blank" rel="noopener noreferrer">www.scaleway.com</a>
      </p>
      <h2>Intellectual property</h2>
      <p>
        The site structure, the game, its texts, logos and graphic elements are the property of
        PrograGames unless stated otherwise. Photographs used in puzzles come from{' '}
        <strong>Pexels</strong> and <strong>Unsplash</strong> and remain the property of their
        respective authors — each photographer is credited on the{' '}
        <Link to="/credits">Photo credits</Link> page. Any unauthorized reproduction of the site
        or its content is prohibited.
      </p>
      <h2>Content reporting</h2>
      <p>
        To report unlawful content or content infringing your rights (photo, display name,
        behavior), use the <Link to="/contact">contact form</Link> (topic &quot;Report&quot;)
        with a precise description of the content. The publisher will process the report as soon
        as possible.
      </p>
      <h2>Personal data and cookies</h2>
      <p>
        Data processing and cookie management are described in the{' '}
        <Link to="/confidentialite">privacy policy</Link>. You can manage your advertising
        preferences via the &quot;Manage cookies&quot; link in the footer.
      </p>
      <h2>Legal documents</h2>
      <ul>
        <li><Link to="/cgu">Terms of use</Link></li>
        <li><Link to="/cgv">Terms of sale</Link></li>
        <li><Link to="/confidentialite">Privacy policy</Link></li>
      </ul>
    </>
  );
}

function PrivacyBodyFr() {
  return (
    <>
      <h2>1. Responsable du traitement</h2>
      <p>
        Florian Augeard — PrograGames, 49000 Angers, France.
        Contact : <Link to="/contact">formulaire de contact</Link> ·{' '}
        <a href="mailto:contact@dezoom.eu">contact@dezoom.eu</a>.
      </p>
      <h2>2. Données collectées</h2>
      <ul>
        <li><strong>Compte</strong> : email, pseudo, avatar (si connexion Google) ;</li>
        <li>
          <strong>Compte invité</strong> : identifiant technique et pseudo aléatoire, sans email ;
          si tu valides ton compte, l&apos;email est ajouté et l&apos;historique invité conservé ;
        </li>
        <li><strong>Gameplay</strong> : scores, série, parties jouées, likes ;</li>
        <li><strong>Achats</strong> : identifiants Stripe (pas de numéro de carte) ;</li>
        <li><strong>Contact</strong> : email et message envoyés via le formulaire de contact ;</li>
        <li><strong>Technique</strong> : IP, logs serveur.</li>
        <li>
          <strong>Mesure d&apos;audience Level Farm</strong> : page vue, lancement de partie et
          clics (hub, réseaux) ; aucune IP n&apos;est conservée, empreinte technique salée côté
          serveur, conservation 400 jours.
        </li>
      </ul>
      <h2>3. Finalités</h2>
      <p>
        Exécution du contrat (fonctionnement du jeu, classements, achats), sécurité (prévention
        de la triche et des abus), réponse à tes demandes de contact, obligations légales
        (comptabilité).
      </p>
      <h2>4. Sous-traitants</h2>
      <ul>
        <li><strong>Stripe</strong> (paiements), <strong>Scaleway</strong> (hébergement), <strong>Mailjet</strong> (emails),</li>
        <li><strong>Google Ireland Limited</strong> (Google AdSense — publicité et mesure d&apos;audience).</li>
      </ul>
      <h2>5. Durées</h2>
      <p>
        Compte actif ; comptes invités inactifs : suppression ou anonymisation possible ;
        achats : 10 ans (comptabilité) ; messages de contact : le temps du traitement ;
        logs : quelques mois.
      </p>
      <h2 id="cookies">6. Cookies et traceurs</h2>
      <p>
        Pour les visiteurs situés dans l&apos;EEE, au Royaume-Uni ou en Suisse, un message de consentement
        certifié Google (CMP) te permet d&apos;accepter, de refuser ou de gérer les cookies publicitaires.
        Ton choix est enregistré par cette plateforme conformément au framework IAB TCF.
      </p>
      <ul>
        <li>
          <strong>Essentiels</strong> : session de jeu, préférences de langue, authentification
          (localStorage). Base légale : intérêt légitime / exécution du contrat.
        </li>
        <li>
          <strong>Mesure d&apos;audience interne</strong> (Level Farm) : pas de cookie, pas de
          stockage local ; respect de Do Not Track. Base légale : intérêt légitime.
        </li>
        <li>
          <strong>Publicitaires (Google AdSense)</strong> : cookies de personnalisation et de mesure
          déposés par Google lorsque tu acceptes le bandeau. Base légale : consentement.
          Durée : jusqu&apos;à 13 mois selon les cookies Google.
        </li>
      </ul>
      <p>
        Tu peux modifier ton choix à tout moment via le lien « Gérer les cookies » en bas de page,
        qui rouvre le message de consentement Google. Si le message n&apos;est pas disponible
        (hors EEE/UK/Suisse ou bloqueur de publicité), aucun cookie publicitaire soumis à
        consentement n&apos;est déposé. Pour les publicités Google :{' '}
        <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">
          paramètres Google Ads
        </a>
        {' '}·{' '}
        <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer">
          technologies publicitaires Google
        </a>
        .
      </p>
      <h2>7. Tes droits (RGPD)</h2>
      <p>
        Tu disposes des droits d&apos;accès, de rectification, d&apos;effacement, de limitation,
        d&apos;opposition et de portabilité sur tes données :
      </p>
      <ul>
        <li>
          <strong>Suppression de compte</strong> : directement depuis ta page profil (section
          « Supprimer mon compte »). Tes données personnelles sont anonymisées immédiatement.
        </li>
        <li>
          <strong>Autres demandes</strong> (accès, rectification, portabilité) : via le{' '}
          <Link to="/contact">formulaire de contact</Link> (motif « Données personnelles »).
          Réponse sous un mois.
        </li>
      </ul>
      <p>
        Si tu estimes que tes droits ne sont pas respectés, tu peux saisir la CNIL :{' '}
        <a href="https://www.cnil.fr/" target="_blank" rel="noopener noreferrer">cnil.fr</a>.
      </p>
      <h2>8. Mineurs</h2>
      <p>16 ans et plus (voir <Link to="/cgu">CGU</Link>).</p>
    </>
  );
}

function PrivacyBodyEn() {
  return (
    <>
      <h2>1. Data controller</h2>
      <p>
        Florian Augeard — PrograGames, 49000 Angers, France.
        Contact: <Link to="/contact">contact form</Link> ·{' '}
        <a href="mailto:contact@dezoom.eu">contact@dezoom.eu</a>.
      </p>
      <h2>2. Data collected</h2>
      <ul>
        <li><strong>Account</strong>: email, display name, avatar (if Google sign-in);</li>
        <li>
          <strong>Guest account</strong>: technical identifier and random display name, no email;
          if you validate your account, the email is added and guest history is kept;
        </li>
        <li><strong>Gameplay</strong>: scores, streak, games played, likes;</li>
        <li><strong>Purchases</strong>: Stripe identifiers (no card numbers stored);</li>
        <li><strong>Contact</strong>: email and message sent through the contact form;</li>
        <li><strong>Technical</strong>: IP, server logs.</li>
        <li>
          <strong>Level Farm audience measurement</strong>: page views, game starts and clicks
          (hub, social) ; no IP is stored, salted technical fingerprint on the server, 400-day
          retention.
        </li>
      </ul>
      <h2>3. Purposes</h2>
      <p>
        Contract performance (game operation, leaderboards, purchases), security (cheat and abuse
        prevention), answering your contact requests, legal obligations (accounting).
      </p>
      <h2>4. Processors</h2>
      <ul>
        <li><strong>Stripe</strong> (payments), <strong>Scaleway</strong> (hosting), <strong>Mailjet</strong> (email),</li>
        <li><strong>Google Ireland Limited</strong> (Google AdSense — advertising and audience measurement).</li>
      </ul>
      <h2>5. Retention</h2>
      <p>
        Active account lifetime; inactive guest accounts may be deleted or anonymized;
        purchases: 10 years; contact messages: for the duration of processing; logs: a few months.
      </p>
      <h2 id="cookies">6. Cookies and trackers</h2>
      <p>
        For visitors in the EEA, UK or Switzerland, a Google-certified consent message (CMP) lets you
        accept, reject or manage advertising cookies. Your choice is stored by this platform under the IAB TCF framework.
      </p>
      <ul>
        <li>
          <strong>Essential</strong>: game session, language preferences, authentication
          (localStorage). Legal basis: legitimate interest / contract performance.
        </li>
        <li>
          <strong>First-party audience measurement</strong> (Level Farm): no cookie, no local
          storage; Do Not Track is honoured. Legal basis: legitimate interest.
        </li>
        <li>
          <strong>Advertising (Google AdSense)</strong>: personalization and measurement cookies
          set by Google when you accept the banner. Legal basis: consent.
          Duration: up to 13 months depending on Google cookies.
        </li>
      </ul>
      <p>
        You can change your choice at any time via the &quot;Manage cookies&quot; link in the
        footer, which reopens the Google consent message. If the message is not available
        (outside EEA/UK/Switzerland or with an ad blocker), no consent-based advertising cookies
        are set. For Google ads:{' '}
        <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">
          Google Ads settings
        </a>
        {' '}·{' '}
        <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer">
          Google advertising technologies
        </a>
        .
      </p>
      <h2>7. Your rights (GDPR)</h2>
      <p>
        You have the right to access, rectify, erase, restrict, object to and port your data:
      </p>
      <ul>
        <li>
          <strong>Account deletion</strong>: directly from your profile page (&quot;Delete my
          account&quot; section). Your personal data is anonymized immediately.
        </li>
        <li>
          <strong>Other requests</strong> (access, rectification, portability): through the{' '}
          <Link to="/contact">contact form</Link> (topic &quot;Personal data&quot;).
          Response within one month.
        </li>
      </ul>
      <p>
        If you believe your rights are not respected, you can lodge a complaint with the French
        authority CNIL:{' '}
        <a href="https://www.cnil.fr/" target="_blank" rel="noopener noreferrer">cnil.fr</a>.
      </p>
      <h2>8. Minors</h2>
      <p>Aged 16 and over (see <Link to="/cgu">Terms of use</Link>).</p>
    </>
  );
}

const BODIES: Record<string, Record<Locale, () => ReactElement>> = {
  cgu: { fr: CGUBodyFr, en: CGUBodyEn },
  cgv: { fr: CGVBodyFr, en: CGVBodyEn },
  mentions: { fr: MentionsBodyFr, en: MentionsBodyEn },
  privacy: { fr: PrivacyBodyFr, en: PrivacyBodyEn },
};

export function LegalBody({ page }: { page: keyof typeof BODIES }) {
  const { locale } = useLocale();
  const Body = BODIES[page][locale];
  return <Body />;
}
